import React from 'react';
import _ from 'lodash';
import { Variable } from 'src/api/types';
import { InfoIcon, Poppins } from 'src/common';
import { useStateSelector } from 'src/redux';
import { formatNumber } from 'src/utils/misc';
import styled from 'styled-components';
import { findVariableByToken } from 'src/components/FormulaInput/shared/formula';
import { getVariableIcon } from 'src/components/FormulaInput/comps/variableIcon';

// `$unpopulated` switches the chip to the red "missing value" treatment
// (red info-circle replacing the type icon) used wherever a variable has no value.
const Capsule = styled.div<{ $unpopulated?: boolean }>`
  background-color: ${({ theme, $unpopulated }) => ($unpopulated ? theme.colors.dropZoneErrBg : theme.colors.aliceBlue)};
  border-radius: 20px;
  border: 1px solid ${({ theme, $unpopulated }) => ($unpopulated ? theme.colors.dropZoneErrBorder : theme.colors.stroke)};
  padding: 2px 10px;
  display: inline-flex;
  align-items: center;
  grid-gap: 6px;

  .vc-icon {
    display: inline-flex;
    align-items: center;
  }
`;

// "No Data" body for the react-tooltip (matches VariableChip).
const noDataHtml = (color: string) =>
  `<div style='display:flex;grid-gap:6px;align-items:center;'><span>No Data</span><svg style='transform:scale(0.8)' xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M9.7963 6.48145V9.99997M9.7963 13.5185H9.80509M18.5926 9.99997C18.5926 14.858 14.6544 18.7963 9.7963 18.7963C4.93824 18.7963 1 14.858 1 9.99997C1 5.14191 4.93824 1.20367 9.7963 1.20367C14.6544 1.20367 18.5926 5.14191 18.5926 9.99997Z" stroke="${color}" stroke-width="1.75926" stroke-linecap="round" stroke-linejoin="round"/></svg></div>`;

interface VariableCapsuleProps {
  id: string;
  variables: Variable[];
  /** Names of workspace/global variables — drives override-icon detection. */
  workspaceVariableNames: Set<string>;
  /** When set (+ tooltipId), reveal the resolved value on hover. */
  showValueOnHover?: boolean;
  tooltipId?: string;
}

// Read-only formula variable chip. Resolution (incl. assessment→workspace
// override) and icon selection are reused VERBATIM from the edit form
// (findVariableByToken + getVariableIcon) so view/library/PDF match editing.
// Unpopulated (unresolved token, or resolved but no value) → red info-circle chip.
export const VariableCapsule: React.FC<VariableCapsuleProps> = ({
  id,
  variables,
  workspaceVariableNames,
  showValueOnHover,
  tooltipId,
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const variable = findVariableByToken(variables, id);

  const hasValue = !!variable && !_.isNil(variable.value);
  const unpopulated = !hasValue;
  // Keep an unresolved (deleted/absent) token legible as #id rather than dropping it.
  const label = variable ? variable.alias || variable.name || `#${variable.id}` : `#${id}`;

  const hoverProps =
    showValueOnHover && tooltipId
      ? {
          'data-tooltip-id': tooltipId,
          'data-tooltip-html': hasValue
            ? formatNumber(variable!.value || 0, { symbol: !!variable!.currency, maxDecimals: 3 })
            : noDataHtml(colors.statusRed),
        }
      : {};

  return (
    <Capsule $unpopulated={unpopulated} {...hoverProps}>
      <span className="vc-icon" data-cy={unpopulated ? 'no-data' : undefined}>
        {unpopulated ? (
          <InfoIcon color="statusRed" cursorDefault style={{ width: 16, height: 16, minWidth: 16 }} />
        ) : (
          getVariableIcon(variable!, workspaceVariableNames)
        )}
      </span>
      <Poppins px={14} weight={500}>
        {label}
      </Poppins>
    </Capsule>
  );
};
